import { useAuth } from "@clerk/clerk-react";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useConvexAuth, useMutation } from "convex/react";
import { MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Id } from "#/../convex/_generated/dataModel";
import { Disc3D } from "#/components/disc/Disc3D";
import { ConversationThread } from "#/components/listing/ConversationThread";
import { Button } from "#/components/ui/button";
import { Sheet, SheetContent } from "#/components/ui/sheet";
import { useSignInModal } from "#/hooks/useSignInModal";
import { COLOR_HEX } from "#/lib/constants";
import { cn } from "#/lib/utils";

export const Route = createFileRoute("/messages")({
	component: MessagesPage,
});

function MessagesPage() {
	const { isLoading, isAuthenticated } = useConvexAuth();
	const { open } = useSignInModal();
	useEffect(() => {
		if (!isLoading && !isAuthenticated) open();
	}, [isLoading, isAuthenticated, open]);
	if (isLoading) return null;
	if (!isAuthenticated) return null;
	return <MessagesContent />;
}

type ConvItem = {
	_id: Id<"conversations">;
	listingId: Id<"listings">;
	buyerId: string;
	sellerId: string;
	discModelName: string | null;
	manufacturerName: string | null;
	discClass: string | null;
	listingPrice: number | null;
	colors: string[];
	imageTopUrl: string | null;
	otherPartyUsername: string | null;
	lastMessageBody: string | null;
	lastMessageAt: number | null;
};

type ListingGroup = {
	listingId: Id<"listings">;
	listingInfo: ConvItem;
	conversations: ConvItem[];
};

function groupByListing(convs: ConvItem[]): ListingGroup[] {
	const map = new Map<string, ConvItem[]>();
	for (const conv of convs) {
		const key = conv.listingId as string;
		if (!map.has(key)) map.set(key, []);
		map.get(key)?.push(conv);
	}
	return Array.from(map.entries())
		.map(([, groupConvs]) => ({
			listingId: groupConvs[0].listingId,
			listingInfo: groupConvs[0],
			conversations: [...groupConvs].sort(
				(a, b) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0),
			),
		}))
		.sort((a, b) => {
			const aLatest = a.conversations[0]?.lastMessageAt ?? 0;
			const bLatest = b.conversations[0]?.lastMessageAt ?? 0;
			return bLatest - aLatest;
		});
}

function getDiscRimColor(colors: string[]): string {
	for (const c of colors) {
		if (COLOR_HEX[c]) return COLOR_HEX[c];
	}
	return "#718096";
}

function MessagesContent() {
	const { t } = useTranslation("messages");
	const { data: conversations = [] } = useQuery(
		convexQuery(api.messages.listConversations, {}),
	);
	const { userId } = useAuth();
	const [activeId, setActiveId] = useState<Id<"conversations"> | null>(null);
	const [sheetOpen, setSheetOpen] = useState(false);

	const allConvs = conversations as ConvItem[];
	const groups = groupByListing(allConvs);
	const activeConv = allConvs.find((c) => c._id === activeId) ?? null;

	function handleSelect(id: Id<"conversations">) {
		setActiveId(id);
		setSheetOpen(true);
	}

	return (
		<div className="page-wrap py-12">
			<div className="flex flex-col gap-8 max-w-2xl">
				<div>
					<p className="sec-tag">{t("header.tag")}</p>
					<h1 className="display-title text-[clamp(36px,4vw,52px)] leading-none text-foreground">
						{t("header.title")}
					</h1>
				</div>

				{groups.length === 0 ? (
					<EmptyInbox />
				) : (
					<div className="flex flex-col gap-3">
						{groups.map((group) => (
							<ListingGroupCard
								key={group.listingId}
								group={group}
								userId={userId ?? null}
								onSelect={handleSelect}
							/>
						))}
					</div>
				)}
			</div>

			<Sheet
				open={sheetOpen}
				onOpenChange={(open) => {
					if (!open) setSheetOpen(false);
				}}
			>
				<SheetContent
					side="right"
					className="w-full sm:max-w-2xl flex flex-col p-0 gap-0"
				>
					{activeConv && userId && (
						<>
							<div className="px-6 pt-8 pb-5 border-b border-border shrink-0">
								<div className="h-0.5 w-8 bg-primary mb-4 rounded-full" />
								<h2
									className="text-2xl font-bold leading-tight text-foreground"
									style={{ fontFamily: "Fraunces, serif" }}
								>
									{activeConv.discModelName ?? "Disc"}
								</h2>
								<div className="flex flex-col gap-0.5 mt-1">
									{activeConv.manufacturerName && (
										<p className="text-sm text-muted-foreground">
											{activeConv.manufacturerName}
											{activeConv.listingPrice != null &&
												` · €${activeConv.listingPrice}`}
										</p>
									)}
									<p className="text-sm text-muted-foreground">
										{activeConv.buyerId === userId
											? `${t("conversation.buyingFrom")} @${activeConv.otherPartyUsername ?? "seller"}`
											: `${t("conversation.sellingTo")} @${activeConv.otherPartyUsername ?? "buyer"}`}
									</p>
									<Link
										to="/listings/$id"
										params={{ id: activeConv.listingId }}
										className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1 self-start"
										onClick={() => setSheetOpen(false)}
									>
										{t("conversation.viewListing")}
									</Link>
								</div>
							</div>
							<div className="flex flex-col flex-1 px-6 py-5 overflow-hidden min-h-0">
								<ConversationThread
									conversationId={activeConv._id}
									currentUserId={userId}
								/>
							</div>
						</>
					)}
				</SheetContent>
			</Sheet>
		</div>
	);
}

function ListingGroupCard({
	group,
	userId,
	onSelect,
}: {
	group: ListingGroup;
	userId: string | null;
	onSelect: (id: Id<"conversations">) => void;
}) {
	const { t } = useTranslation("messages");
	const { listingInfo, conversations } = group;
	const rimColor = getDiscRimColor(listingInfo.colors);

	return (
		<div className="rounded-xl border border-border overflow-hidden">
			{/* Listing header */}
			<div className="flex items-center gap-4 px-5 py-5 bg-muted/20">
				<div
					className="shrink-0 flex items-center justify-center"
					style={{ width: 80, height: 80 }}
				>
					{listingInfo.imageTopUrl ? (
						<Disc3D
							topUrl={listingInfo.imageTopUrl}
							rimColor={rimColor}
							discClass={listingInfo.discClass}
							size={80}
							disableTilt
						/>
					) : (
						<div
							className="rounded-full bg-muted flex items-center justify-center"
							style={{
								width: 80,
								height: 80,
								border: `2px solid ${rimColor}40`,
							}}
						>
							<MessageCircle className="size-5 text-muted-foreground/50" />
						</div>
					)}
				</div>

				<div className="flex flex-col gap-0.5 min-w-0 flex-1">
					{listingInfo.manufacturerName && (
						<span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
							{listingInfo.manufacturerName}
						</span>
					)}
					<span
						className="font-bold text-lg text-foreground leading-tight truncate"
						style={{ fontFamily: "Fraunces, serif" }}
					>
						{listingInfo.discModelName ?? "Disc"}
					</span>
					<div className="flex items-center gap-2 mt-0.5">
						{listingInfo.listingPrice != null && (
							<span className="text-sm font-medium text-primary-accessible">
								€{listingInfo.listingPrice}
							</span>
						)}
						<Link
							to="/listings/$id"
							params={{ id: listingInfo.listingId }}
							className="text-xs text-muted-foreground hover:text-foreground transition-colors"
							onClick={(e) => e.stopPropagation()}
						>
							{t("conversation.viewListing")}
						</Link>
					</div>
				</div>
			</div>

			{/* Conversation rows */}
			<div className="divide-y divide-border">
				{conversations.map((conv) => (
					<ConversationRow
						key={conv._id}
						conv={conv}
						userId={userId}
						onSelect={onSelect}
					/>
				))}
			</div>
		</div>
	);
}

function ConversationRow({
	conv,
	userId,
	onSelect,
}: {
	conv: ConvItem;
	userId: string | null;
	onSelect: (id: Id<"conversations">) => void;
}) {
	const { t } = useTranslation("messages");
	const { data: conversation } = useQuery(
		convexQuery(api.messages.getConversation, { conversationId: conv._id }),
	);
	const markRead = useMutation(api.messages.markConversationRead);

	const unreadCount =
		conversation?.messages.filter(
			(msg) => msg.senderId !== userId && msg.readAt === undefined,
		).length ?? 0;

	const isBuyer = userId ? conv.buyerId === userId : true;

	function handleClick() {
		onSelect(conv._id);
		markRead({ conversationId: conv._id });
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			className="flex items-center gap-3 w-full px-5 py-3 text-left transition-colors hover:bg-muted/40 bg-background"
		>
			<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
				<MessageCircle className="size-3.5 text-primary" />
			</div>
			<div className="flex flex-col gap-0.5 flex-1 min-w-0">
				<div className="flex items-center gap-1.5">
					<span
						className={cn(
							"text-[10px] font-bold uppercase tracking-widest",
							isBuyer ? "text-primary-accessible" : "text-muted-foreground",
						)}
					>
						{isBuyer
							? t("conversation.buyingFrom")
							: t("conversation.sellingTo")}
					</span>
					<span className="text-xs font-semibold text-foreground">
						@{conv.otherPartyUsername ?? "..."}
					</span>
				</div>
				{conv.lastMessageBody && (
					<p className="truncate text-sm text-muted-foreground">
						{conv.lastMessageBody}
					</p>
				)}
			</div>
			<div className="flex items-center gap-2 shrink-0">
				{unreadCount > 0 && (
					<span className="flex size-5 items-center justify-center rounded-full bg-primary-accessible text-[10px] font-bold text-primary-foreground">
						{unreadCount}
					</span>
				)}
				{conv.lastMessageAt && (
					<span className="text-xs text-muted-foreground">
						{new Date(conv.lastMessageAt).toLocaleDateString()}
					</span>
				)}
			</div>
		</button>
	);
}

function EmptyInbox() {
	const { t } = useTranslation("messages");
	return (
		<div className="rounded-xl border border-dashed border-border py-24 flex flex-col items-center text-center">
			<div className="relative mb-5 flex size-16 items-center justify-center rounded-full bg-muted">
				<MessageCircle className="size-7 text-muted-foreground/35" />
			</div>
			<p className="font-semibold text-foreground">{t("empty.title")}</p>
			<p className="mt-1 text-sm text-muted-foreground max-w-xs">
				{t("empty.description")}
			</p>
			<Button className="mt-6" asChild>
				<Link
					to="/listings"
					search={{
						sort: "newest" as const,
						q: "",
						discClass: [],
						condition: [],
						color: [],
						shipsTo: [],
						country: [],
						plasticType: [],
						priceMin: "",
						priceMax: "",
						weightMin: "",
						weightMax: "",
						discModelIds: [],
						manufacturerIds: [],
						speedMin: "",
						speedMax: "",
						glideMin: "",
						glideMax: "",
						turnMin: "",
						turnMax: "",
						fadeMin: "",
						fadeMax: "",
						distanceKm: "",
					}}
				>
					{t("empty.browse")}
				</Link>
			</Button>
		</div>
	);
}
